// utils/realtimeManager/subscription/index.ts — createResilientSubscription

import { supabase } from '../../../supabase';
import type {RealtimeChannel} from '@supabase/supabase-js';
import type {ResilientChannel} from '../types.js';
import type {SubscriptionConfig} from '../types.js';
import { createRetryScheduler } from './retry.js';
import { createSubscriber } from './subscribe.js';
import type { SubscriptionState } from './subscribe.js';

/**
 * Create a resilient realtime subscription that automatically reconnects on failure
 */
export function createResilientSubscription(config: SubscriptionConfig): ResilientChannel {
  const state: SubscriptionState = {
    channel: null,
    isUnsubscribed: false,
    // Generation counter — incremented each time we create a new channel.
    // Callbacks from old (removed) channels check this and no-op if stale.
    generation: 0,
  };

  const retry = createRetryScheduler({
    isUnsubscribed: () => state.isUnsubscribed,
    subscribe: () => subscribe(),
    channelName: config.channelName,
  });

  const subscribe = createSubscriber(config, state, retry);

  const unsubscribe = () => {
    state.isUnsubscribed = true;
    retry.dispose();
    state.generation++; // invalidate any in-flight channel callbacks
    if (state.channel) {
      const oldChannel = state.channel;
      state.channel = null;
      try {
        supabase.removeChannel(oldChannel);
      } catch {
        // Ignore
      }
    }
  };

  // Initial subscription
  subscribe();

  return { channel: state.channel, unsubscribe };
}
